import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorMessage } from '../../../core/models/ErrorMessage';
import { MapperUtils } from '../../../core/utils/MapperUtils';
import { AlertService } from '../../../core/services/alert.service';
import { CsvReaderService } from '../../../core/services/csv-reader.service';

/**
 * Shared CSV import modal. The caller supplies the wording, the sample file and the two operations that
 * differ per entity: how to parse a row and how to send the parsed rows.
 */
@Component({
  selector: 'app-import-csv',
  template: `
    <app-modal>
      <ng-container class="title">{{ titleKey | transloco | uppercase }}</ng-container>
      <ng-container class="actions">
        <button type="button" class="button-tertiary text-secondary" aria-label="Close" (mousedown)="activeModal.dismiss()">
          {{ 'action.close' | transloco | uppercase }}
        </button>
      </ng-container>

      <ng-container class="content">
        <form [formGroup]="form">
          <app-errors [errors]="errors | async"></app-errors>
          <div class="mb-3">
            <!-- the native file input is hidden and driven by the label: its own button text comes from
                 the browser language, so it cannot follow the language chosen in the portal -->
            <input
              required
              type="file"
              class="visually-hidden"
              id="importCsvFile"
              formControlName="file"
              (change)="changeFile($event)"
              accept=".csv"
            />
            <label for="importCsvFile" class="button-secondary">{{ 'action.chooseFile' | transloco }}</label>
            <span class="ms-2" *ngIf="fileName">{{ fileName }}</span>
            <app-input-errors class="w-100" [control]="form.get('file')"></app-input-errors>
            <small class="form-text text-muted d-block">{{ csvPatternKey | transloco }}</small>
            <ul class="form-text text-muted ps-3 mt-1 mb-1" *ngIf="supportsAccounts">
              <li>{{ 'action.importAccountHint' | transloco }}</li>
              <li>{{ 'action.importPasswordHint' | transloco }}</li>
            </ul>
            <a [href]="'assets/downloads/' + sampleFile">
              <small>{{ 'action.downloadCsvSample' | transloco }}</small>
            </a>
          </div>

          <div class="form-check d-flex align-items-center" *ngIf="supportsAccounts">
            <input
              type="checkbox"
              formControlName="requiredToChangePasswordOnNextLogin"
              class="form-check-input"
              id="requiredToChangePasswordOnNextLogin"
            />
            <label for="requiredToChangePasswordOnNextLogin" class="form-check-label">
              {{ 'action.requirePasswordChangeOnFirstLogin' | transloco }}
            </label>
          </div>
        </form>
      </ng-container>

      <ng-container class="footer">
        <app-save-button
          buttonClass="button-primary"
          [isLoading]="isLoading | async"
          [isDisabled]="form.invalid || !(rows$ | async)?.length"
          (savedClicked)="submit()"
        >
          {{ 'action.import' | transloco | uppercase }}
        </app-save-button>
      </ng-container>
    </app-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportCsvComponent<T> implements OnInit, OnDestroy {
  @Input() titleKey: string;
  @Input() csvPatternKey: string;
  @Input() sampleFile: string;
  /** The imported entity can hold a TransFollow Drive account: shows the account notes and checkbox. */
  @Input() supportsAccounts = false;
  @Input() noRowsKey = 'action.importNoRows';
  @Input() unreadableFileKey = 'action.importUnreadableFile';
  @Input() parse: (csv: string) => Observable<T[]>;
  @Input() submitRows: (rows: T[], requiredToChangePasswordOnNextLogin: boolean) => Observable<object>;
  @Input() isLoading: Observable<boolean>;

  fileName = '';
  errors = new BehaviorSubject<ErrorMessage[]>(null);

  private rows = new BehaviorSubject<T[]>(null);
  readonly rows$ = this.rows.asObservable();
  private fileSelected = new Subject<File>();
  private subscription = new Subscription();

  form = new UntypedFormGroup({
    file: new UntypedFormControl(null, [Validators.required]),
    requiredToChangePasswordOnNextLogin: new UntypedFormControl(false),
  });

  constructor(
    public activeModal: NgbActiveModal,
    private csvReaderService: CsvReaderService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    // switchMap, so picking a second file abandons the first read instead of letting whichever
    // finishes last win - a large file followed by a small one would otherwise submit the large one
    this.subscription.add(
      this.fileSelected
        .pipe(
          switchMap((file) =>
            this.csvReaderService.importFromCsv(file).pipe(
              switchMap((csv) => this.parse(csv)),
              catchError(() => {
                this.errors.next([{ message: this.unreadableFileKey }]);
                // nothing emitted, so rows stays as changeFile left it: null, button disabled
                return EMPTY;
              })
            )
          )
        )
        .subscribe((rows) => {
          this.rows.next(rows);
          if (!rows.length) {
            this.errors.next([{ message: this.noRowsKey }]);
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  submit(): void {
    const rows = this.rows.getValue();
    if (this.form.invalid || !rows?.length) {
      return;
    }

    const requiredToChangePasswordOnNextLogin = !!this.form.get('requiredToChangePasswordOnNextLogin').value;
    this.submitRows(rows, requiredToChangePasswordOnNextLogin)
      .pipe(take(1))
      .subscribe(
        () => {
          this.activeModal.close('success');
          this.alertService.success('alert.importSuccess');
        },
        (response: HttpErrorResponse) => {
          this.errors.next(MapperUtils.errorResponseToErrorMessages(response));
          this.alertService.error('alert.importFail');
        }
      );
  }

  changeFile(event: Event): void {
    const target = event.target as HTMLInputElement;
    // cleared first so picking a second file can never submit the previous one's rows
    this.rows.next(null);
    this.errors.next(null);

    if (!target.files[0]?.name) {
      this.fileName = '';
      return;
    }

    this.fileName = target.files[0].name;
    this.fileSelected.next(target.files.item(0));
  }
}
